import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, MailIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { readImages } from '../../utils/files';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-231" data-mp="2jpx8s644d5tz" />;
  const customer = customerById(item.customerId);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-232" data-mp="2xppt9jp1ypti">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-233" data-mp="2xoeu5x6jy68q">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-234" data-mp="37epy3jq8kffm" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-235" data-mp="32hzjz1xzrtwu">
        <div data-id="element-236" data-mp="3gfxu1v5a546j">
          <p className="text-sm text-ink-muted" data-id="element-237" data-mp="3ghr13bjt1qf2">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-238" data-mp="3cfdu6jtbnmgp">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-239" data-mp="3jg7r4lfila79">
            {customer?.name}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-240" data-mp="3tgrt5qauyiqj">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-241" data-mp="63foaa7rgw2k">
          <StatusBadge status={item.status} data-id="element-242" data-mp="62w74vpxqbpl" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-243" data-mp="b28t7e8zfer4">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-244" data-mp="13wx6qa7e6u3q" />
            Slet sag
          </button>
        </div>
      </header>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-245" data-mp="z5ynb1r3xfw9">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-246" data-mp="v43oq50k9gm6">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-247" data-mp="v290agq2z6dc" />
        </div>
        <div className="mt-6" data-id="element-248" data-mp="1du1x6m89tbqc">
          <StatusTimeline status={item.status} data-id="element-249" data-mp="1dta2ddmhzi14" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-250" data-mp="18v8hcn5ih8n0">
        <div className="space-y-5" data-id="element-251" data-mp="2ghck8d9p3np">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-252" data-mp="2hiwz3g6b2i3">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-253" data-mp="3tl7lsmnckzkv">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-254" data-mp="1ldaz8mhqgrz1">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-255" data-mp="1q64214tn5q8h">
              <div data-id="element-256" data-mp="1carv7z27o9b7">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-257" data-mp="1hbz3dz89naie">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-258" data-mp="1h9l7xp9obwab">{item.category}</dd>
              </div>
              <div data-id="element-259" data-mp="24zpea4e8ya50">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-260" data-mp="292vfuyqezd55">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-261" data-mp="1v3mciznb4dil">{item.address || '—'}</dd>
              </div>
              <div data-id="element-262" data-mp="2027307crbitu">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-263" data-mp="201nu1g5344ry">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-264" data-mp="jnxn81mkz2em">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-265" data-mp="oov48avutqej" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-266" data-mp="q2cxjcweo4t1">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-267" data-mp="l5w0ft78ag3t">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-268" data-mp="3tlphc95h1tle">
              <label htmlFor="ny-note" className="sr-only" data-id="element-269" data-mp="2dbphqrhivoa">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-270" data-mp="3asvdobdatnwh" />
              <Button type="submit" variant="dark" data-id="element-271" data-mp="1ohuxlzsb852e">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-272" data-mp="qumgegzcdi67">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-273" data-mp="vtqu9kx7o42v">
                  <div data-id="element-274" data-mp="2c6o0yhb9hf71">
                    <p className="text-xs text-ink-muted" data-id="element-275" data-mp="2g8iz5cxwbcgl">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-276" data-mp="2gadhqklp2shi">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-277" data-mp="ju8ou7ratlg">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-278" data-mp="3i0v8ajw18zc0" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-279" data-mp="3906vfsidl50p">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-280" data-mp="342dqt3wq0j2j">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-281" data-mp="177iob82m1xfn">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-282" data-mp="128o4c2swq2ty">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-283" data-mp="b3bwhtk7gpu9">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-284" data-mp="b5y2horg6xad">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-285" data-mp="1cr0mdvx1zf1">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-286" data-mp="1hrs6xg0u1ndo">
                  <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-287" data-mp="2x957iuxeohdc" />
                  Upload
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={async event => {
                  const input = event.target;
                  const files = await readImages(input.files);
                  if (files.length) addPhotos(item.id, files, stage);
                  input.value = '';
                }} data-id="element-288" data-mp="3c1yx596e6dvq" />
                </label>
              </div>
            </div>

            <div className="mt-5 space-y-6" data-id="element-289" data-mp="20kz9wrywip8k">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-290" data-mp="2m0nq1r55oy96">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-291" data-mp="2h0j6ttcv5pf9">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-292" data-mp="2c1omgmz8zst1">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-293" data-mp="34pu9xhhorvwk">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-294" data-mp="34p2dk4g9lu58" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-295" data-mp="2q1pquceq1id9">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-296" data-mp="3v1nvfgho9l2q" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-297" data-mp="ds939r6h38gh">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-298" data-mp="mjrgpuc1ba5v">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-299" data-mp="mkt510i29t5j">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-300" data-mp="rht06gddhbhv">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-301" data-mp="wjicdzcbzv7j">{customer?.name}</p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-302" data-mp="11htn0caga9qt">
              <li data-id="element-303" data-mp="bsd5nkiffvok">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-304" data-mp="br21hjl2klwx">{customer?.email || '—'}</li>
              <li data-id="element-305" data-mp="6veaqzqxvkpb">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-306" data-mp="lipp19ei9it4">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-307" data-mp="ll2e06ioktfo">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-308" data-mp="gq6aa9430hmq" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-309" data-mp="pkky502oh0hg">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-310" data-mp="14htt03r6wmdx" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-311" data-mp="zja8m7s8vino">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-312" data-mp="1gt4ad6ptzpn7">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-313" data-mp="1lrnz1evt7fj9">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-314" data-mp="172ruvswvimnx">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-315" data-mp="170faza7k2288">
                <dt className="text-ink-soft" data-id="element-316" data-mp="1bzs0k2pt9nei">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-317" data-mp="y1214gfirbmz">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-318" data-mp="12483hczigio6">
                <dt className="text-ink-soft" data-id="element-319" data-mp="o3nkogtlimuo">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-320" data-mp="o58g08nqi9tl">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-321" data-mp="t4l40wqcdmxr">
                <dt className="text-ink-soft" data-id="element-322" data-mp="2iiq8ma2zxwvh">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-323" data-mp="2ng9kj3c81xk9">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-324" data-mp="neicmsymr3h5" />
          </section>
        </aside>
      </div>
    </div>;
}